/**
 * PRP-MCP-5 G9: the Original | Enhanced switch for an imported estimate.
 *
 * `OriginalOrEnhanced` wraps the page's line views: Enhanced (default) renders
 * them unchanged; Original replaces them with a read-only list of the import as
 * it arrived (rooms, lines, quantities, notes, imported prices). No edit,
 * Enhance, chat tag or undo control appears in Original, and switching saves
 * nothing. The switch is absent when `useOriginalView` says it is not offered.
 */
import type { ReactNode } from 'react';

import type { ParsedEstimateResponse, ParsedLineItem } from '../../types';
import { fmtMoney } from '../lib/format';
import type { EstimateVersion, OriginalView } from '../lib/useOriginalView';
import { Card } from './ui/card';
import { Segmented } from './ui/segmented';
import { Skeleton } from './ui/skeleton';

export function OriginalEnhancedToggle({
  value,
  onChange,
}: {
  value: EstimateVersion;
  onChange: (_v: EstimateVersion) => void;
}) {
  return (
    <div className="flex items-center gap-[10px] mb-[10px]" data-testid="original-enhanced-toggle">
      <Segmented<EstimateVersion>
        value={value}
        onValueChange={onChange}
        options={[
          { value: 'original', label: 'Original' },
          { value: 'enhanced', label: 'Enhanced' },
        ]}
      />
      <span className="text-[11.5px] text-[var(--tx-lo)]">
        {value === 'original'
          ? 'The estimate exactly as imported. Read only. Export still uses the enhanced version.'
          : 'The current estimate, with every change made since import.'}
      </span>
    </div>
  );
}

function money(n: number | null | undefined): string {
  return typeof n === 'number' ? fmtMoney(n, true) : '—';
}

function lineNotes(li: ParsedLineItem & { note?: string | null }): string[] {
  return [li.note, ...(li.notes ?? [])].filter((n): n is string => !!n && !!n.trim());
}

/** The import as it arrived, read only. */
export function OriginalLines({ result }: { result: ParsedEstimateResponse }) {
  const rooms = result.estimate.rooms ?? [];
  return (
    <Card className="p-0 overflow-hidden" data-testid="original-lines">
      <table className="w-full text-[12px]">
        <thead>
          <tr className="text-left text-[10.5px] uppercase tracking-[.05em] text-[var(--tx-lo)]">
            <th className="px-3 py-2">#</th>
            <th className="px-3 py-2">Code</th>
            <th className="px-3 py-2">Description</th>
            <th className="px-3 py-2 text-right">Qty</th>
            <th className="px-3 py-2">Unit</th>
            <th className="px-3 py-2 text-right">Unit price</th>
            <th className="px-3 py-2 text-right">Total</th>
          </tr>
        </thead>
        {rooms.map((room, ri) => (
          <tbody key={`${room.group_code}-${ri}`}>
            <tr>
              <td colSpan={7} className="px-3 pt-3 pb-1 font-[580] text-[var(--tx-hi)]">
                {room.group_description || room.group_code}
              </td>
            </tr>
            {room.line_items.map((li, i) => {
              const notes = lineNotes(li);
              return (
                <tr
                  key={`${li.line_number}-${i}`}
                  data-testid="original-line"
                  className="border-t border-[var(--bd-subtle)] align-top"
                >
                  <td className="px-3 py-[6px] font-[var(--font-mono)] text-[var(--tx-lo)]">{li.line_number}</td>
                  <td className="px-3 py-[6px] font-[var(--font-mono)]">
                    {`${li.category ?? ''} ${li.selector ?? ''}`.trim() || '—'}
                  </td>
                  <td className="px-3 py-[6px] text-[var(--tx-hi)]">
                    {li.description}
                    {notes.map((n, k) => (
                      <div key={k} className="text-[11px] text-[var(--tx-lo)]">
                        {n}
                      </div>
                    ))}
                  </td>
                  <td className="px-3 py-[6px] text-right font-[var(--font-mono)]">{li.quantity}</td>
                  <td className="px-3 py-[6px]">{li.unit}</td>
                  <td className="px-3 py-[6px] text-right font-[var(--font-mono)]">{money(li.unit_cost)}</td>
                  <td className="px-3 py-[6px] text-right font-[var(--font-mono)]">{li.price_note || money(li.rcv)}</td>
                </tr>
              );
            })}
          </tbody>
        ))}
      </table>
    </Card>
  );
}

/** The switch plus either the original (read only) or the page's own views. */
export function OriginalOrEnhanced({ ov, children }: { ov: OriginalView; children: ReactNode }) {
  if (!ov.visible) return <>{children}</>;
  let body: ReactNode = children;
  if (ov.version === 'original') {
    body = ov.original ? (
      <OriginalLines result={ov.original} />
    ) : ov.error ? (
      <Card className="p-4 text-[12.5px] text-[var(--bad)]">{ov.error}</Card>
    ) : (
      <Skeleton className="h-[240px] w-full" />
    );
  }
  return (
    <>
      <OriginalEnhancedToggle value={ov.version} onChange={ov.setVersion} />
      {body}
    </>
  );
}
